import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FaBoxOpen, FaCartShopping, FaDumbbell, FaGasPump, FaIndianRupeeSign, FaLayerGroup, FaMobileScreen } from 'react-icons/fa6';
import PlatformHub from './PlatformHub';
import { FUEL_URL } from '../products/effyFuelContent';
import { buildWebSiteSchema } from '../seo/seoSchemas';
import './HomePage.css';

const TITLE = 'GST Billing, Inventory & Order Software | EffyVenture';
const DESCRIPTION = 'GST billing, inventory and order management software for Indian small businesses: GST invoices, stock, sales reports and customer dues. Also Effy Fuel for petrol bunks.';
const SHORT_DESCRIPTION = 'GST billing, inventory and order management software for Indian small businesses.';

// Descriptive links to the main keyword pages, shown in the topics section below the intro.
const topicLinks = [
  { to: '/products/effy-order/gst-billing-software', label: 'GST billing software', text: 'Invoices with CGST, SGST and IGST from your HSN tax profile.' },
  { to: '/products/effy-order/inventory-management-software', label: 'Inventory management software', text: 'Stock that updates on every sale, purchase and return.' },
  { to: '/products/effy-order/order-management-software', label: 'Order management software', text: 'Order and payment status from creation to delivery.' },
  { to: '/products/effy-order/stock-management-software', label: 'Stock management software', text: 'Stock value, low stock, daily movement and expiry reports.' },
  { to: '/products/effy-order/gst-report-software', label: 'GST report software', text: 'A tax summary for the dates you choose, ready for your accountant.' },
  { to: '/products/effy-order/sales-report-software', label: 'Sales report software', text: 'Sales, closing and category-wise reports with profit.' },
  { to: '/products/effy-order/sales-management-software', label: 'Sales management software', text: 'Quotations, orders, invoices, payments and customer dues.' },
  { to: '/products/effy-order/invoice-billing-software', label: 'Invoice billing software', text: 'Print or share invoices and record payments.' },
  { to: '/products/effy-order/billing-software-for-small-business', label: 'Billing software for small business', text: 'One browser app for the whole counter.' },
  { to: '/billing-software-for-tamil-nadu-shops', label: 'Billing software for Tamil Nadu shops', text: 'Use the app in English or Tamil.' },
];

// Live apps: `external` links leave this site (Effy Fuel is a separate app on its own host).
const liveProducts = [
  {
    Icon: FaBoxOpen, title: 'Effy Orders', external: false,
    desc: 'Run your orders, stock and customers from one place.',
    points: ['Orders with GST invoices and delivery challans', 'Stock tracking with barcode scan and low-stock alerts', 'Customer history and sales reports'],
    appHref: '/products/order-tracking/login', signupHref: '/products/order-tracking/signup',
  },
  {
    Icon: FaGasPump, title: 'Effy Fuel', external: true,
    desc: 'Close every shift with confidence.',
    points: ['Know exactly what you sold in every shift', 'Keep track of customers who buy on credit', 'See your petrol, diesel and oil stock at a glance'],
    appHref: `${FUEL_URL}/login`, signupHref: `${FUEL_URL}/signup`,
  },
];

const upcomingProducts = [
  { Icon: FaCartShopping, title: 'E-Commerce', desc: 'Online store management with product listings, cart, checkout, and payment integrations.' },
  { Icon: FaDumbbell, title: 'Gym App', desc: 'Gym management with member tracking, class scheduling, automated billing, and workout logging.' },
];

const trustPoints = [
  { Icon: FaLayerGroup, label: 'Real apps you can use today' },
  { Icon: FaIndianRupeeSign, label: 'Built for GST bills and daily bunk shifts' },
  { Icon: FaMobileScreen, label: 'Works on phone, tablet and desktop' },
];

// Router link for pages on this site, plain anchor for other hosts.
function AppLink({ to, external, children, ...props }) {
  return external ? <a href={to} {...props}>{children}</a> : <Link to={to} {...props}>{children}</Link>;
}

export default function HomePage() {
  return (
    <main className="home">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.effyventure.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.effyventure.com/" />
        <meta property="og:site_name" content="EffyVenture" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content="https://www.effyventure.com/logo/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={SHORT_DESCRIPTION} />
        <meta name="twitter:image" content="https://www.effyventure.com/logo/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(buildWebSiteSchema())}</script>
      </Helmet>
      {/* Hero */}
      <section className="home__hero">
        <div className="home__hero-inner">
          <div className="home__hero-content">
            <div className="eyebrow home__hero-tag">EffyVenture Platform</div>
            <h1 className="home__hero-title">
              GST billing, order and inventory software<br />
              <span className="home__hero-accent">for Indian small businesses</span>
            </h1>
            <p className="home__hero-subtitle">
              Practical, purpose-built solutions to help small businesses manage daily operations, simplify work, and grow with confidence.
            </p>
            <div className="home__hero-actions">
              <a href="#products" className="btn btn-primary btn-lg"
                onClick={e => { e.preventDefault(); document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' }); }}>
                Explore Products →
              </a>
              <Link to="/contact" className="btn btn-secondary btn-lg">
                Get in Touch
              </Link>
            </div>
          </div>
          <div className="home__hero-visual" aria-hidden="true">
            <PlatformHub />
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <ul className="home__trust" aria-label="Why EffyVenture">
        {trustPoints.map(({ Icon, label }) => (
          <li key={label} className="home__trust-item">
            <Icon aria-hidden="true" />
            <span>{label}</span>
          </li>
        ))}
      </ul>

      {/* Plain intro text for visitors and search engines, with links to the detail pages */}
      <section className="home__intro">
        <h2 className="home__intro-title">Simple software for billing, stock and petrol bunks</h2>
        <p className="home__intro-text">
          EffyVenture builds practical software for Indian small businesses. Effy Order is{' '}
          <Link to="/products/effy-order/gst-billing-software">GST billing software</Link> that also manages your orders,
          customers and <Link to="/products/effy-order/inventory-management-software">inventory management</Link>.
          Create GST invoices with CGST, SGST and IGST, print delivery challans, scan barcodes at the counter and let
          stock update on every sale. Effy Fuel is <Link to="/petrol-bunk-software-tamil-nadu">petrol bunk software</Link>{' '}
          that closes each shift from meter readings, keeps credit customer records and shows your fuel and oil stock.
          New to GST bills? Read our guide to the <Link to="/gst-invoice-format-india">GST invoice format in India</Link>.
          Both apps work in the browser on a phone, tablet or computer.
        </p>
      </section>

      {/* Keyword page links for visitors and search engines */}
      <section className="home__topics" aria-labelledby="home-topics-title">
        <h2 id="home-topics-title" className="home__topics-title">What you can do with Effy Order</h2>
        <ul className="home__topics-grid">
          {topicLinks.map((topic) => (
            <li key={topic.to} className="home__topic">
              <Link to={topic.to} className="home__topic-link">{topic.label}</Link>
              <span className="home__topic-text">{topic.text}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Products */}
      <section id="products" className="home__features">
        <div className="home__section-header">
          <h2>Our Products</h2>
          <p>Pick the app that fits your business.</p>
        </div>
        <div className="home__features-grid home__features-grid--live">
          {liveProducts.map(p => (
            <div key={p.title} className="home__feature-card home__feature-card--active">
              <div className="home__feature-top">
                <span className="home__feature-icon"><p.Icon /></span>
                <span className="badge badge-completed">Live</span>
              </div>
              <h3>{p.title}</h3>
              <p>{p.desc}</p>
              <ul className="home__feature-points">
                {p.points.map(point => <li key={point}>{point}</li>)}
              </ul>
              <div className="home__feature-actions">
                <AppLink to={p.appHref} external={p.external} className="btn btn-secondary btn-sm" aria-label={`Open ${p.title}`}>Open app</AppLink>
                <AppLink to={p.signupHref} external={p.external} className="home__feature-link" aria-label={`Sign up for ${p.title}`}>Sign up →</AppLink>
              </div>
            </div>
          ))}
        </div>
        <div className="home__features-grid home__features-grid--soon">
          {upcomingProducts.map(p => (
            <div key={p.title} className="home__feature-card home__feature-card--soon">
              <div className="home__feature-top">
                <span className="home__feature-icon"><p.Icon /></span>
                <span className="badge badge-initiated">Soon</span>
              </div>
              <h3>{p.title}</h3>
              <p>{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="home__cta">
        <div className="home__cta-inner">
          <h2>Pick your product</h2>
          <p>Free for your first month. Takes a minute to sign up.</p>
          <div className="home__cta-actions">
            <Link to="/products/order-tracking/signup" className="btn btn-primary btn-lg">Start with Effy Orders</Link>
            <a href={`${FUEL_URL}/signup`} className="btn btn-primary btn-lg">Start with Effy Fuel</a>
          </div>
          <Link to="/contact" className="home__hero-link">Not sure? Get in touch</Link>
        </div>
      </section>

    </main>
  );
}
